import type { TicketTypeTemplate, AdditionalDetailsValues } from '../types/ticketTypeTemplate';

/**
 * Pick the ticket-type template an existing work item was raised from.
 *
 * A saved job only records the template's `group` (the create payload sends
 * `jobType: template.group`, which lands on `cr001_problem_type`) — never its
 * `ticketTypeId`. A group is a whole family: "M365 Support" alone covers a
 * dozen templates, so `templates.find(t => t.group === problemType)` returns an
 * arbitrary sibling. That is not a cosmetic mismatch — the Additional Details
 * panel renders *that* template's `additionalFields`, so an M365 Offboard
 * ticket opened for edit showed "Assign new M365 license" inputs and the saved
 * `{ userName: 'James Lamb' }` had nowhere to land, i.e. a blank details panel.
 *
 * So narrow the group with the two things the job does persist:
 *
 *  - **its subject**, matched against each candidate's `subjectTemplate` with
 *    the placeholders opened out to wildcards ("Offboard User - {userName}"
 *    matches "Offboard User - James Lamb"). This is the strongest signal, and
 *    the only one for templates that collect no fields.
 *  - **the keys of its saved `templateFieldValues`**, counted against the
 *    fields each candidate declares.
 *
 * Ties fall back to the first template in the group, which is what the plain
 * `.find()` did — this only ever narrows a guess, never widens one.
 */

const SUBJECT_MATCH_WEIGHT = 10;

/**
 * Turn a subject template into an anchored matcher. `{key}` becomes a wildcard
 * and a `{?key}`…`{/key}` conditional block becomes an optional group, so a
 * template's own output always matches its pattern.
 *
 * Returns null when the template carries no literal text to anchor on (e.g. a
 * bare "{userName}"), since such a pattern matches every subject and would make
 * the signal worse than useless.
 */
export function subjectTemplateToPattern(subjectTemplate: string): RegExp | null {
  let literalLength = 0;
  let pattern = '';
  const token = /\{\?(\w+)\}|\{\/(\w+)\}|\{(\w+)\}/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  const pushLiteral = (text: string) => {
    if (!text) return;
    literalLength += text.trim().length;
    pattern += text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // eslint-disable-next-line no-cond-assign
  while ((match = token.exec(subjectTemplate)) !== null) {
    pushLiteral(subjectTemplate.slice(cursor, match.index));
    if (match[1] !== undefined) pattern += '(?:';
    else if (match[2] !== undefined) pattern += ')?';
    else pattern += '.*?';
    cursor = match.index + match[0].length;
  }
  pushLiteral(subjectTemplate.slice(cursor));

  if (literalLength === 0) return null;
  try {
    return new RegExp(`^${pattern}$`, 's');
  } catch {
    // An unbalanced conditional marker can leave the group syntax broken; a
    // template we can't compile simply contributes no signal.
    return null;
  }
}

export interface ResolveTemplateForWorkItemArgs {
  templates: TicketTypeTemplate[];
  /** The job's `cr001_problem_type` — the template group it was raised under. */
  problemType?: string | null;
  /** The job's subject. */
  title?: string | null;
  /** The job's saved `cr001_template_field_values`. */
  templateFieldValues?: AdditionalDetailsValues | null;
}

export function resolveTemplateForWorkItem({
  templates,
  problemType,
  title,
  templateFieldValues,
}: ResolveTemplateForWorkItemArgs): TicketTypeTemplate | null {
  const group = (problemType ?? '').trim().toLowerCase();
  if (!group) return null;

  const candidates = templates.filter(t => (t.group ?? '').trim().toLowerCase() === group);
  if (candidates.length <= 1) return candidates[0] ?? null;

  const subject = (title ?? '').trim();
  const savedKeys = Object.entries(templateFieldValues ?? {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key]) => key);

  let best = candidates[0];
  let bestScore = -1;

  for (const candidate of candidates) {
    let score = 0;

    if (subject && candidate.subjectTemplate) {
      const pattern = subjectTemplateToPattern(candidate.subjectTemplate);
      if (pattern?.test(subject)) score += SUBJECT_MATCH_WEIGHT;
    }

    if (savedKeys.length > 0) {
      const declared = new Set((candidate.additionalFields ?? []).map(f => f.fieldKey));
      score += savedKeys.filter(key => declared.has(key)).length;
    }

    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}
